import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { client } from '@xinfer/api-client';
import type { Account } from '@xinfer/api-client';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DataTable,
  PageHeader,
  usePageTitle,
  type ColumnDef,
} from '@xinfer/ui';
import { Download } from 'lucide-react';
import { TokenComposition } from '../components/TokenComposition';
import { explainKvTokenSeconds, formatInteger } from '../lib/format';

interface MetricRow {
  metric: string;
  value: number;
  meaning: string;
}

export function Usage({ account }: { account: Account }) {
  usePageTitle('Usage');

  const usage = useQuery({
    queryKey: ['usage', account.id],
    queryFn: () => client.usage(account.id),
  });

  const rows = useMemo<MetricRow[]>(() => {
    if (!usage.data) return [];
    return [
      { metric: 'Requests', value: usage.data.requests, meaning: 'completed inference calls' },
      {
        metric: 'Input tokens',
        value: usage.data.input_tokens,
        meaning: 'prompt, cached and uncached together',
      },
      {
        metric: 'Cached input tokens',
        value: usage.data.cached_input_tokens,
        meaning: 'of the above, served from the prefix cache and billed at a lower rate',
      },
      { metric: 'Output tokens', value: usage.data.output_tokens, meaning: 'generated' },
      {
        metric: 'KV-token-seconds',
        value: usage.data.kv_token_seconds,
        meaning: explainKvTokenSeconds(usage.data.kv_token_seconds),
      },
      {
        metric: 'Peak KV tokens',
        value: usage.data.peak_kv_tokens,
        meaning: 'largest context held at once',
      },
    ];
  }, [usage.data]);

  const columns = useMemo<ColumnDef<MetricRow, unknown>[]>(
    () => [
      {
        accessorKey: 'metric',
        header: 'Metric',
        cell: ({ row }) => <span className="font-medium">{row.original.metric}</span>,
      },
      {
        accessorKey: 'value',
        header: 'Value',
        cell: ({ row }) => <span className="font-mono">{formatInteger(row.original.value)}</span>,
      },
      {
        accessorKey: 'meaning',
        header: 'Meaning',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-muted-foreground">{row.original.meaning}</span>
        ),
      },
    ],
    [],
  );

  function exportCsv() {
    if (!usage.data) return;
    const csvRows = [
      ['metric', 'value'],
      ['requests', String(usage.data.requests)],
      ['input_tokens', String(usage.data.input_tokens)],
      ['cached_input_tokens', String(usage.data.cached_input_tokens)],
      ['output_tokens', String(usage.data.output_tokens)],
      ['kv_token_seconds', String(usage.data.kv_token_seconds)],
      ['peak_kv_tokens', String(usage.data.peak_kv_tokens)],
    ];
    const csv = csvRows.map((r) => r.join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `xinfer-usage-${account.id}.csv`;
    // Both of these matter. A detached anchor is not reliably clickable
    // everywhere, and revoking the object URL in the same task can cancel the
    // download that `click()` only just scheduled — the symptom being a button
    // that does nothing, silently, in some browsers and not others.
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  return (
    <>
      <PageHeader
        title="Usage"
        description="Everything the service meters, including what it does not yet charge for."
        actions={
          <Button variant="outline" onClick={exportCsv} disabled={!usage.data}>
            <Download />
            Export CSV
          </Button>
        }
      />

      <DataTable
        columns={columns}
        data={rows}
        loading={usage.isLoading}
        error={usage.isError}
        errorMessage="Usage could not be read. This is the ledger, not the serving path — inference is unaffected."
        getRowId={(row) => row.metric}
      />

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Token composition</CardTitle>
          <CardDescription>
            The same figures as a proportion. Cached prompt tokens are a subset of the prompt
            total, so this plots uncached prompt, cached prompt and output rather than adding the
            three raw fields together.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {usage.data ? (
            <TokenComposition usage={usage.data} />
          ) : (
            <p className="text-sm text-muted-foreground">Loading…</p>
          )}
        </CardContent>
      </Card>

      <p className="mt-6 text-sm text-muted-foreground">
        These are period totals. A breakdown over time needs the api to serve rollups rather than
        totals, which it does not do yet.
      </p>
    </>
  );
}
